import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import Api from '../../services/Api';
import { AUTH_TOKEN_NAME } from '../../controllers/AutenticacaoController';
import useAuth from '../../hooks/useAuth';

interface Tela {
  tela_chave: string;
  tela_nome: string;
  tela_grupo: string;
  /** Ações separadas por vírgula. VAZIO significa TODAS — ver `podeFazer`. */
  acoes?: string;
}

interface PermissaoContexto {
  /** `null` enquanto não se sabe — ver o comentário sobre "carregando" abaixo. */
  telas: Set<string> | null;
  carregando: boolean;
  podeVer: (chave: string | null) => boolean;
  /**
   * Se o usuário pode EXERCER uma ação numa tela: 'visualizar', 'incluir', 'editar',
   * 'excluir', 'cancelar'. Para desabilitar botão — não é proteção.
   */
  podeFazer: (chave: string | null, acao: string) => boolean;
  recarregar: () => Promise<void>;
}

const Contexto = createContext<PermissaoContexto>(null!);

/**
 * As telas que o usuário logado pode ver.
 *
 * POR QUE NÃO VEM DENTRO DO TOKEN
 *
 * Com 31 telas o JWT incharia e, pior, só mudaria no próximo login: o administrador
 * tiraria um acesso e a pessoa continuaria com ele até o token expirar. Aqui o front
 * pergunta uma vez, e o servidor reconfere a cada requisição no middleware.
 *
 * O QUE FAZER ENQUANTO NÃO CHEGOU — e esta é a decisão que mais aparece na tela
 *
 * `telas` nasce `null`, que significa "ainda não sei", e é diferente de "conjunto vazio".
 * Mostrar o menu inteiro e depois encolher dá um pisca feio e deixa a pessoa ver, por um
 * instante, o que ela não tem. Esconder tudo e depois mostrar parece que o sistema
 * quebrou. Com `null` o menu simplesmente não se desenha até a resposta chegar — é a
 * única alternativa que nunca mostra informação errada.
 *
 * SE A CHAMADA FALHAR, `telas` vira conjunto VAZIO e o menu fica vazio. Parece severo,
 * mas o contrário — liberar tudo quando não se consegue perguntar — transformaria uma
 * falha de rede no caminho para ver o sistema inteiro. E quem de fato tem acesso recupera
 * com um F5, que refaz a pergunta.
 */
export function PermissaoProvider({ children }: { children: React.ReactNode }) {
  const [telas, setTelas] = useState<Set<string> | null>(null);
  /* Por tela, as ações concedidas. CONJUNTO VAZIO = SEM RESTRIÇÃO = todas, que é o
     mesmo vazio da tabela no banco e o estado de todo papel que ninguém configurou. */
  const [acoes, setAcoes] = useState<Map<string, Set<string>>>(new Map());
  const [carregando, setCarregando] = useState(false);

  const recarregar = useCallback(async () => {
    try {
      if (!localStorage.getItem(AUTH_TOKEN_NAME)) {
        // Sem token não há o que perguntar: é a tela de login.
        setTelas(null);
        setAcoes(new Map());
        return;
      }
    } catch {
      setTelas(new Set());
      setAcoes(new Map());
      return;
    }

    setCarregando(true);

    try {
      const resposta = await Api.get('api/acesso/MinhasTelas');
      const lista: Tela[] = Array.isArray(resposta?.data) ? resposta.data : [];

      setTelas(new Set(lista.map((t) => t.tela_chave)));

      const mapa = new Map<string, Set<string>>();
      lista.forEach((t) => {
        const marcadas = (t.acoes ?? '')
          .split(',')
          .map((a) => a.trim())
          .filter(Boolean);
        mapa.set(t.tela_chave, new Set(marcadas));
      });
      setAcoes(mapa);
    } catch {
      // O 401 já é tratado pelo interceptador do Api.ts, que leva para o login.
      // Qualquer outra falha cai aqui, e o lado seguro é não liberar nada.
      setTelas(new Set());
      setAcoes(new Map());
    } finally {
      setCarregando(false);
    }
  }, []);

  const { currentUser } = useAuth();

  /* Recarrega ao montar E a cada troca de usuário.
     `currentUser` na dependência não é enfeite: o login é uma navegação do React, sem
     recarregar a página, então o efeito de montagem já tinha rodado - com o app ainda sem
     token - e nunca mais rodaria. Quem acabasse de entrar ficaria com o menu VAZIO até
     apertar F5. No logout acontece o inverso e também é desejado: volta a null. */
  useEffect(() => {
    recarregar();
  }, [recarregar, currentUser]);

  /** `null` = rota que não exige permissão (Home, login). Sempre liberada. */
  const podeVer = useCallback(
    (chave: string | null) => {
      if (chave === null) return true;
      if (telas === null) return false; // ainda não sei: não afirmo que pode
      return telas.has(chave);
    },
    [telas]
  );

  /**
   * CONJUNTO VAZIO = TODAS. É a regra de compatibilidade que percorre o sistema
   * inteiro: papel que ninguém configurou não tem linha de ação nenhuma no banco, e
   * isso significa que ele faz tudo na tela que tem — o comportamento que existia
   * antes de a granularidade existir. Tratar vazio como "nada" trancaria todo mundo
   * para fora no dia em que isto subisse.
   *
   * E ISTO NÃO PROTEGE NADA: é conforto, para o usuário não clicar num botão que vai
   * tomar 403. Quem protege é a PROC_ACESSO_ROTA_VERIFICAR, no servidor.
   */
  const podeFazer = useCallback(
    (chave: string | null, acao: string) => {
      if (!podeVer(chave)) return false;
      if (chave === null) return true;

      const permitidas = acoes.get(chave);
      if (!permitidas || permitidas.size === 0) return true;

      return permitidas.has(acao);
    },
    [acoes, podeVer]
  );

  return (
    <Contexto.Provider value={{ telas, carregando, podeVer, podeFazer, recarregar }}>
      {children}
    </Contexto.Provider>
  );
}

export function usePermissao() {
  return useContext(Contexto);
}
